import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  ListAlt as ListAltIcon,
  Comment as CommentIcon,
  CheckCircle as YesIcon,
  Cancel as NoIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const SectionCard = ({ icon, title, children }) => (
  <Card variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Box display="flex" alignItems="center" mb={2}>
        {icon}
        <Typography
          variant="h6"
          component="div"
          sx={{ ml: 1, fontWeight: "bold" }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

// A reusable component to display each question's details
const QuestionDetailBlock = ({ label, value, comments }) => (
  <Grid item xs={12} sx={{ mb: 2 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
    >
      <Typography variant="body1" sx={{ flexGrow: 1, pr: 2 }}>
        {label}
      </Typography>
      <Chip
        icon={value ? <YesIcon /> : <NoIcon />}
        label={value ? "Yes" : "No"}
        color={value ? "error" : "success"}
        size="small"
      />
    </Box>
    {comments && (
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          backgroundColor: "grey.50",
          borderRadius: 1,
          border: "1px solid #eee",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: "pre-wrap" }}
        >
          {comments}
        </Typography>
      </Box>
    )}
  </Grid>
);

// A specific component for questions with multi-select options
const MultiSelectDetailBlock = ({ label, value, options, comments }) => (
  <Grid item xs={12} sx={{ mb: 2 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
    >
      <Typography variant="body1" sx={{ flexGrow: 1, pr: 2 }}>
        {label}
      </Typography>
      <Chip
        icon={value ? <YesIcon /> : <NoIcon />}
        label={value ? "Yes" : "No"}
        color={value ? "error" : "success"}
        size="small"
      />
    </Box>
    {value && (
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          backgroundColor: "grey.50",
          borderRadius: 1,
          border: "1px solid #eee",
        }}
      >
        {options && options.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: comments ? 1.5 : 0,
            }}
          >
            {options.map((opt, index) => (
              <Chip key={index} label={opt} size="small" variant="outlined" />
            ))}
          </Box>
        )}
        {comments && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {comments}
          </Typography>
        )}
      </Box>
    )}
  </Grid>
);

const ChokingAssessmentDetailsModal = ({ open, handleClose, assessment }) => {
  if (!assessment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, backgroundColor: "grey.100" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Choking Risk Assessment Details
          </Typography>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: { xs: 1, sm: 3 }, backgroundColor: "#f7f9fc" }}
      >
        <SectionCard
          icon={<PersonIcon color="primary" />}
          title="Client Information"
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {assessment.client_name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Care Plan:</strong> {assessment.care_plan_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Assessed by:</strong> {assessment.created_by_name} on{" "}
                {format(new Date(assessment.created_at), "dd MMM yyyy, p")}
              </Typography>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          icon={<ListAltIcon color="primary" />}
          title="Assessment Breakdown"
        >
          <Grid container spacing={1}>
            <QuestionDetailBlock
              label="Frequently coughs before, during, or after eating/drinking?"
              value={assessment.q_coughs}
              comments={assessment.q_coughs_comments}
            />
            <QuestionDetailBlock
              label="Already has a diagnosis of dysphagia?"
              value={assessment.q_is_diagnosed}
              comments={assessment.q_is_diagnosed_comments}
            />
            <QuestionDetailBlock
              label="Choking incident in the last 12 months?"
              value={assessment.q_choking_hist}
              comments={assessment.q_choking_hist_comments}
            />
            <QuestionDetailBlock
              label="Pneumonia or recurrent chest infections in the last 12 months?"
              value={assessment.q_chest_infection_hist}
              comments={assessment.q_chest_infection_hist_comments}
            />
            <QuestionDetailBlock
              label="Able to chew a normal diet?"
              value={assessment.q_can_chew}
              comments={assessment.q_can_chew_comments}
            />
            <QuestionDetailBlock
              label="Food and/or drink currently modified?"
              value={assessment.q_is_diet_modified}
              comments={assessment.q_is_diet_modified_comments}
            />
            <QuestionDetailBlock
              label="Has a diagnosis of Pica?"
              value={assessment.q_has_pica}
              comments={assessment.q_has_pica_comments}
            />
            <QuestionDetailBlock
              label="Has behaviours that may increase choking risk?"
              value={assessment.q_has_risk_behaviours}
              comments={assessment.q_has_risk_behaviours_comments}
            />

            <MultiSelectDetailBlock
              label="Significant change in the last 12 months?"
              value={assessment.q_has_change}
              options={assessment.q_change_options}
              comments={assessment.q_change_comments}
            />
            <MultiSelectDetailBlock
              label="Experiencing side effects from medication that could increase choking risk?"
              value={assessment.q_has_side_effects}
              options={assessment.q_side_effect_options}
              comments={assessment.q_side_effect_comments}
            />
          </Grid>
        </SectionCard>

        {assessment.summary && (
          <SectionCard
            icon={<CommentIcon color="primary" />}
            title="Overall Summary and Action Plan"
          >
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {assessment.summary}
            </Typography>
          </SectionCard>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "16px 24px" }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChokingAssessmentDetailsModal;
